import React from 'react';
import { MapPin, Calendar, Plane, Users } from 'lucide-react';

// Импорт данных о городах
import { russianCities, internationalCities } from './data/cities';

const FlightParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  aircraftTypes,
  flightClasses
}) => {
  // Объединяем списки российских и международных городов
  const cities = [...russianCities, ...internationalCities].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Город отправления */}
      <div>
        <label htmlFor="departureCity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <MapPin className="w-4 h-4 mr-2 text-blue-500" />
          Город отправления
        </label>
        <select
          id="departureCity"
          name="departureCity"
          value={formData.departureCity}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.departureCity ? 'input-error' : ''}`}
        >
          <option value="">Выберите город</option>
          <optgroup label="Города России">
            {russianCities.map((city) => (
              <option key={city.id} value={city.name}>
                {city.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="Международные города">
            {internationalCities.map((city) => (
              <option key={city.id} value={city.name}>
                {city.name}
              </option>
            ))}
          </optgroup>
        </select>
        {errors.departureCity && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.departureCity}
          </p>
        )}
      </div>

      {/* Город прибытия */}
      <div>
        <label htmlFor="arrivalCity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <MapPin className="w-4 h-4 mr-2 text-blue-500" />
          Город прибытия
        </label>
        <select
          id="arrivalCity"
          name="arrivalCity"
          value={formData.arrivalCity}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.arrivalCity ? 'input-error' : ''}`}
        >
          <option value="">Выберите город</option>
          <optgroup label="Города России">
            {russianCities.map((city) => (
              <option key={city.id} value={city.name}>
                {city.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="Международные города">
            {internationalCities.map((city) => (
              <option key={city.id} value={city.name}>
                {city.name}
              </option>
            ))}
          </optgroup>
        </select>
        {errors.arrivalCity && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.arrivalCity}
          </p>
        )}
      </div>

      {/* Дата отправления */}
      <div>
        <label htmlFor="departureDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-blue-500" />
          Дата отправления
        </label>
        <input
          type="date"
          id="departureDate"
          name="departureDate"
          value={formData.departureDate}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.departureDate ? 'input-error' : ''}`}
          min={new Date().toISOString().split('T')[0]}
        />
        {errors.departureDate && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.departureDate}
          </p>
        )}
      </div>

      {/* Тип самолета */}
      <div>
        <label htmlFor="aircraftType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Plane className="w-4 h-4 mr-2 text-blue-500" />
          Тип самолета
        </label>
        <select
          id="aircraftType"
          name="aircraftType"
          value={formData.aircraftType}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.aircraftType ? 'input-error' : ''}`}
        >
          <option value="">Выберите тип самолета</option>
          {aircraftTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name} ({type.examples})
            </option>
          ))}
        </select>
        {errors.aircraftType && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.aircraftType}
          </p>
        )}
      </div>

      {/* Флажок обратного полета */}
      <div className="col-span-full">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isRoundTrip"
            name="isRoundTrip"
            checked={formData.isRoundTrip}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <label htmlFor="isRoundTrip" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Обратный билет
          </label>
        </div>
      </div>

      {/* Дата возвращения (отображается только при обратном билете) */}
      {formData.isRoundTrip && (
        <div className="md:col-start-1">
          <label htmlFor="returnDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-2 text-blue-500" />
            Дата возвращения
          </label>
          <input
            type="date"
            id="returnDate"
            name="returnDate"
            value={formData.returnDate}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.returnDate ? 'input-error' : ''}`}
            min={formData.departureDate}
          />
          {errors.returnDate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.returnDate}
            </p>
          )}
        </div>
      )}

      {/* Класс обслуживания */}
      <div>
        <label htmlFor="flightClass" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Users className="w-4 h-4 mr-2 text-blue-500" />
          Класс обслуживания
        </label>
        <select
          id="flightClass"
          name="flightClass"
          value={formData.flightClass}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          {flightClasses.map((classType) => (
            <option key={classType.id} value={classType.id}>
              {classType.name}
            </option>
          ))}
        </select>
      </div>

      {/* Тип полета (прямой или с пересадками) */}
      <div className="col-span-full">
        <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Тип маршрута
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="flightType"
              value="direct"
              checked={formData.flightType === 'direct'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Прямой рейс
            </span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="flightType"
              value="connecting"
              checked={formData.flightType === 'connecting'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              С пересадкой
            </span>
          </label>
        </div>
        {errors.flightType && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.flightType}
          </p>
        )}
      </div>
    </div>
  );
};

export default FlightParametersSection;